import React from 'react';
import { PillIcon, BookIcon, ClipboardHeartIcon, CloudSunIcon, MoonIcon, StepsIcon, DumbbellIcon, RouteIcon, FlowerIcon, PencilIcon, AlertIcon, DropletIcon, ChartIcon, HeartIcon, UtensilsIcon, ActivityIcon, PhoneIcon, IconProps } from '../components/icons';
/* ---------- tint system ---------- */
/* Tailwind needs literal class names, so tints are explicit maps. */
export type Tint = 'teal' | 'coral' | 'butter' | 'sage' | 'sky' | 'lilac';
export type TintClasses = {
  soft: string;
  solid: string;
  text: string;
  border: string;
  iconOn: string;
};
export const TINTS: Record<Tint, TintClasses> = {
  teal: {
    soft: 'bg-teal-soft',
    solid: 'bg-teal',
    text: 'text-teal-deep',
    border: 'border-[#17695E2b]',
    iconOn: 'text-cream'
  },
  coral: {
    soft: 'bg-coral-soft',
    solid: 'bg-coral',
    text: 'text-coral-deep',
    border: 'border-[#E0785633]',
    iconOn: 'text-cream'
  },
  butter: {
    soft: 'bg-butter-soft',
    solid: 'bg-butter',
    text: 'text-butter-deep',
    border: 'border-[#E9B94940]',
    iconOn: 'text-cream'
  },
  sage: {
    soft: 'bg-sage-soft',
    solid: 'bg-sage',
    text: 'text-sage-deep',
    border: 'border-[#7E9E7833]',
    iconOn: 'text-cream'
  },
  sky: {
    soft: 'bg-sky-soft',
    solid: 'bg-sky',
    text: 'text-sky-deep',
    border: 'border-[#6E9BC033]',
    iconOn: 'text-cream'
  },
  lilac: {
    soft: 'bg-lilac-soft',
    solid: 'bg-lilac',
    text: 'text-lilac-deep',
    border: 'border-[#9D8BC233]',
    iconOn: 'text-cream'
  }
};
/* ---------- features (dashboard grid) ---------- */
export type Feature = {
  id: string;
  title: string;
  hint: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
  /* optional status badge shown on the right of the card (e.g. progress) */
  badge?: string;
};
export const FEATURES: Feature[] = [{
  id: 'journey',
  title: 'Journey',
  hint: 'Week 7 of 12 — going well',
  icon: RouteIcon,
  tint: 'coral',
  badge: '60%'
}, {
  id: 'tracking',
  title: 'Track',
  hint: 'Inhaler, symptoms & peak flow',
  icon: ChartIcon,
  tint: 'sky',
  badge: '4'
}, {
  id: 'plans',
  title: 'Plans',
  hint: 'Your asthma action plan',
  icon: ClipboardHeartIcon,
  tint: 'sage',
  badge: '3'
}, {
  id: 'library',
  title: 'Library',
  hint: '3 new articles for you',
  icon: BookIcon,
  tint: 'teal',
  badge: 'new'
}, {
  id: 'nhs',
  title: 'NHS',
  hint: 'GP, prescriptions & services',
  icon: HeartIcon,
  tint: 'coral'
}, {
  id: 'move',
  title: 'Move',
  hint: 'Steps, exercise & activity',
  icon: ActivityIcon,
  tint: 'butter'
}, {
  id: 'relax',
  title: 'Relax & Sleep',
  hint: '10-min wind-down session',
  icon: MoonIcon,
  tint: 'lilac'
}, {
  id: 'eating',
  title: 'Healthy Eating',
  hint: 'Meals, nutrition & hydration',
  icon: UtensilsIcon,
  tint: 'sage'
}, {
  id: 'weather',
  title: 'Weather',
  hint: 'Pollen is high today',
  icon: CloudSunIcon,
  tint: 'sky',
  badge: 'Rain 45%'
}];
/* ---------- contextual recommendations (dashboard) ---------- */
export type Recommendation = {
  id: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
  title: string;
  body: string;
  action: string;
  modal: ModalKind;
};
export const RECOMMENDATIONS: Recommendation[] = [{
  id: 'pollen',
  icon: FlowerIcon,
  tint: 'coral',
  title: 'Grass pollen high today',
  body: 'Keep your reliever near by.',
  action: 'See forecast',
  modal: 'weather'
}, {
  id: 'symptoms',
  icon: PencilIcon,
  tint: 'teal',
  title: 'How are you doing?',
  body: "It's been 3 days",
  action: 'Log now',
  modal: 'tracking'
}, {
  id: 'preventer',
  icon: PillIcon,
  tint: 'sky',
  title: 'Preventer due at 8am',
  body: 'Take your morning dose to stay ahead of symptoms.',
  action: 'Mark taken',
  modal: 'tracking'
}, {
  id: 'plan-review',
  icon: ClipboardHeartIcon,
  tint: 'sage',
  title: 'Action plan refreshed',
  body: 'Your nurse updated your asthma action plan yesterday.',
  action: 'Review plan',
  modal: 'plans'
}, {
  id: 'sleep',
  icon: MoonIcon,
  tint: 'lilac',
  title: 'Restless night logged',
  body: 'Sleep dipped below your average — try a wind-down tonight.',
  action: 'Wind down',
  modal: 'relax'
}, {
  id: 'reading',
  icon: BookIcon,
  tint: 'teal',
  title: 'New for you',
  body: 'A 3-minute read on managing pollen-season flare-ups.',
  action: 'Read now',
  modal: 'article'
}, {
  id: 'journey',
  icon: RouteIcon,
  tint: 'butter',
  title: 'Day 12 of your journey',
  body: 'You’re building a steady tracking streak — keep it going.',
  action: 'See journey',
  modal: 'journey'
}];
/* ---------- favourites (dashboard — quick tool access) ---------- */
export type Favourite = {
  id: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
  title: string;
  subtitle: string;
  modal: ModalKind;
  /* deep-link a modal to a specific view while keeping the picker in the
     back stack (e.g. open the action plan with a back arrow to the picker) */
  start?: string;
};
export const FAVOURITES: Favourite[] = [{
  id: 'medications',
  icon: PillIcon,
  tint: 'sky',
  title: 'My Medications',
  subtitle: 'Your prescriptions',
  modal: 'tracking'
}, {
  id: 'health-record',
  icon: ClipboardHeartIcon,
  tint: 'sage',
  title: 'My Health Record',
  subtitle: 'GP record & history',
  modal: 'plans'
}, {
  id: 'care-plan',
  icon: ClipboardHeartIcon,
  tint: 'teal',
  title: 'My Asthma Action Plan',
  subtitle: 'Your asthma action plan',
  modal: 'plans',
  start: 'action-plan'
}, {
  id: 'library',
  icon: BookIcon,
  tint: 'coral',
  title: 'Health Library',
  subtitle: 'Articles & guides for you',
  modal: 'article'
}, {
  id: 'relax',
  icon: MoonIcon,
  tint: 'lilac',
  title: 'Relax & Sleep',
  subtitle: '10-min wind-down sessions',
  modal: 'relax'
}];
/* ---------- support (dashboard — help & emergency access) ---------- */
export type Support = {
  id: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
  title: string;
  subtitle: string;
  /* opens a modal (e.g. the emergency action plan) */
  modal?: ModalKind;
  /* deep-link the modal to a specific view, keeping the picker in the back
     stack (e.g. emergency steps with a back arrow to the plan picker) */
  start?: string;
  /* dials a number on tap */
  tel?: string;
};
export const SUPPORT: Support[] = [{
  id: 'emergency-plan',
  icon: AlertIcon,
  tint: 'coral',
  title: 'Emergency Plan',
  subtitle: 'Your asthma attack steps',
  modal: 'plans',
  start: 'emergency'
}, {
  id: 'call-999',
  icon: PhoneIcon,
  tint: 'coral',
  title: 'Call 999',
  subtitle: 'Emergency services',
  tel: '999'
}, {
  id: 'call-111',
  icon: PhoneIcon,
  tint: 'sky',
  title: 'Call 111',
  subtitle: 'NHS urgent advice',
  tel: '111'
}, {
  id: 'call-gp',
  icon: PhoneIcon,
  tint: 'sage',
  title: 'Call GP',
  subtitle: 'Your registered practice',
  tel: ''
}, {
  id: 'call-samaritans',
  icon: PhoneIcon,
  tint: 'lilac',
  title: 'Call Samaritans',
  subtitle: 'Free, 24/7 emotional support',
  tel: '116123'
}, {
  id: 'call-asthma-lung',
  icon: PhoneIcon,
  tint: 'butter',
  title: 'Call Asthma & Lung UK Support',
  subtitle: 'Specialist nurse helpline',
  tel: '0300 222 5800'
}];
/* ---------- universal result cards (chat) ---------- */
export type ModalKind = 'weather' | 'article' | 'tracking' | 'journey' | 'plans' | 'relax' | null;
export type ResultCardData = {
  kind: string;
  meta: string;
  title: string;
  summary: string;
  confidence?: number;
  hasNarration?: boolean;
  tint: Tint;
  icon: (p: IconProps) => React.ReactElement;
  modal: ModalKind;
  /* semantic routing — where inside the modal this card lands
     (e.g. care plans: 'action-plan' | 'med-schedule' | 'emergency';
      relax: 'wind-down' | 'sleep-log' | 'rain-sounds';
      tracking: a tracker id, or 'history') */
  start?: string;
};
export const RESULT_CARDS: Record<string, ResultCardData> = {
  weather: {
    kind: 'weather',
    meta: 'Weather & pollen',
    title: 'Tomorrow in Hackney',
    summary: '21° sunny intervals · grass pollen high',
    confidence: 0.96,
    tint: 'sky',
    icon: CloudSunIcon,
    modal: 'weather'
  },
  article: {
    kind: 'article',
    meta: 'Health library',
    title: 'What is a preventer inhaler?',
    summary: '3 min read · narrated by Sunny',
    confidence: 0.93,
    hasNarration: true,
    tint: 'teal',
    icon: BookIcon,
    modal: 'article'
  },
  tracking: {
    kind: 'tracking',
    meta: 'Tracking',
    title: 'Log inhaler use',
    summary: 'Reliever · quick log',
    tint: 'coral',
    icon: PillIcon,
    modal: 'tracking'
  },
  /* the history card — "how's my tracking going?" lands here */
  trackinghistory: {
    kind: 'trackinghistory',
    meta: 'Tracking · History',
    title: 'Your last two weeks',
    summary: '35 logs · steady preventer routine · one flare-up',
    confidence: 0.95,
    tint: 'coral',
    icon: ChartIcon,
    modal: 'tracking',
    start: 'history'
  },
  journey: {
    kind: 'journey',
    meta: 'Journey · Week 7 of 12',
    title: 'Pollen season tactics',
    summary: 'Resume where you left off · 3 parts left · 5-day streak',
    confidence: 0.97,
    hasNarration: true,
    tint: 'coral',
    icon: RouteIcon,
    modal: 'journey'
  },
  careplan: {
    kind: 'careplan',
    meta: 'Care plans',
    title: 'Your Asthma Action Plan',
    summary: 'Green · Amber · Red zones — reviewed with your nurse 12 May',
    confidence: 0.95,
    hasNarration: true,
    tint: 'sage',
    icon: ClipboardHeartIcon,
    modal: 'plans',
    start: 'action-plan'
  },
  medschedule: {
    kind: 'medschedule',
    meta: 'Care plans · Medication',
    title: 'Your medication schedule',
    summary: 'Preventer morning & evening · reliever as needed',
    confidence: 0.94,
    tint: 'sky',
    icon: PillIcon,
    modal: 'plans',
    start: 'med-schedule'
  },
  /* the emergency card deliberately has NO confidence chrome — match
     percentages have no place on a safety-critical flow */
  emergency: {
    kind: 'emergency',
    meta: 'Emergency',
    title: 'Asthma attack — what to do',
    summary: '5 calm steps · Sunny will read them with you',
    tint: 'coral',
    icon: AlertIcon,
    modal: 'plans',
    start: 'emergency'
  },
  /* ---- relax & sleep (card-flows §5) ---- */
  winddown: {
    kind: 'winddown',
    meta: 'Relax & Sleep',
    title: 'Wind-down session',
    summary: '10 min · narrated by Sunny · on your device',
    confidence: 0.96,
    hasNarration: true,
    tint: 'lilac',
    icon: MoonIcon,
    modal: 'relax',
    start: 'wind-down'
  },
  sleeplog: {
    kind: 'sleeplog',
    meta: 'Relax & Sleep · Sleep log',
    title: "Log last night's sleep",
    summary: 'Bedtime, wake time & how it felt · 20 seconds',
    confidence: 0.94,
    tint: 'lilac',
    icon: PencilIcon,
    modal: 'relax',
    start: 'sleep-log'
  },
  rainsounds: {
    kind: 'rainsounds',
    meta: 'Relax & Sleep',
    title: 'Wind-down with rain sounds',
    summary: '10 min · rain soundscape · narrated by Sunny',
    confidence: 0.92,
    hasNarration: true,
    tint: 'sky',
    icon: DropletIcon,
    modal: 'relax',
    start: 'rain-sounds'
  }
};
/* ---------- scripted conversation ---------- */
/* Mock of: query → semantic search → curated result card.
   The assistant only ever FINDS content — it never generates advice. */
export type Script = {
  id: string;
  chip: string;
  spoken: string;
  reply: string;
  card: string;
  /* top-priority routing — no thinking pause, shorter delays
     (the emergency flow must never make the user wait) */
  priority?: boolean;
  /* open the card's modal automatically after the reply — no tap,
     no confirmation (emergency routing skips everything) */
  autoOpen?: boolean;
};
export const SCRIPTS: Script[] = [{
  id: 'pollen',
  chip: "Tomorrow's pollen forecast",
  spoken: "What's the pollen forecast for tomorrow?",
  reply: 'Tomorrow looks bright, but grass pollen will be high. I found the full forecast for you.',
  card: 'weather'
}, {
  id: 'inhaler',
  chip: 'What is a preventer inhaler?',
  spoken: 'What is a preventer inhaler?',
  reply: 'I found an article in your library that explains preventer inhalers really clearly.',
  card: 'article'
}, {
  id: 'log',
  chip: 'Log my inhaler use',
  spoken: 'Log that I used my inhaler just now.',
  reply: "Of course — here's a quick log for that.",
  card: 'tracking'
} /* the history query — the assistant reads the log store, the user just asks */, {
  id: 'tracking-history',
  chip: "How's my tracking going?",
  spoken: "How's my tracking going?",
  reply: "Really well — 35 logs in two weeks, and your preventer routine hasn't missed a day. Here's everything together.",
  card: 'trackinghistory'
}, {
  id: 'journey',
  chip: 'Resume my journey',
  spoken: 'Resume my journey.',
  reply: "You're 2 parts into Pollen season tactics — I'll take you straight back to where you left off.",
  card: 'journey'
}, {
  id: 'careplan',
  chip: 'Show my care plan',
  spoken: 'Show my care plan.',
  reply: 'Here it is — your asthma action plan, the one you reviewed with your nurse in May.',
  card: 'careplan'
}, {
  id: 'preventer-when',
  chip: 'When do I take my preventer?',
  spoken: 'When do I take my preventer?',
  reply: "Your preventer is two puffs, morning and evening. Here's your full schedule.",
  card: 'medschedule'
} /* ---- relax & sleep (card-flows §5) — conversational entry skips the picker ---- */, {
  id: 'cant-sleep',
  chip: "I can't sleep",
  spoken: "I can't sleep.",
  reply: "I'm here. Let's slow everything down together — your wind-down session is ready.",
  card: 'winddown'
}, {
  id: 'sleep-log',
  chip: 'Log my sleep',
  spoken: 'Log my sleep from last night.',
  reply: 'Of course — how did last night go?',
  card: 'sleeplog'
}, {
  id: 'rain-sounds',
  chip: 'Play the rain sounds one',
  spoken: 'Play the rain sounds one.',
  reply: 'Rain it is — your wind-down session with rain in the background.',
  card: 'rainsounds'
},
/* the safety-critical one: top-priority routing, no intermediary, no
confirmation — the emergency steps open by themselves */
{
  id: 'breathe',
  chip: "I'm struggling to breathe",
  spoken: "I'm struggling to breathe.",
  reply: "Okay. I'm right here with you — opening your emergency steps now.",
  card: 'emergency',
  priority: true,
  autoOpen: true
}];
/* ---------- weather detail (bottom modal) ---------- */
export const WEATHER = {
  location: 'Hackney, London',
  day: 'Tomorrow · Wednesday 3 June',
  temp: 21,
  condition: 'Sunny intervals',
  pollen: {
    level: 'High',
    pct: 78,
    note: 'Grass pollen peaks between 2–6pm. If pollen is one of your triggers, your care plan suggests taking your preventer before heading out.'
  },
  stats: [{
    label: 'Humidity',
    value: '54%'
  }, {
    label: 'Wind',
    value: '9 mph'
  }, {
    label: 'Air quality',
    value: 'Good'
  }, {
    label: 'UV index',
    value: '6 · High'
  }],
  hours: [{
    t: '9am',
    temp: 16
  }, {
    t: '11am',
    temp: 18
  }, {
    t: '1pm',
    temp: 20
  }, {
    t: '3pm',
    temp: 21
  }, {
    t: '5pm',
    temp: 20
  }, {
    t: '7pm',
    temp: 18
  }]
};
/* ---------- educational article (bottom modal) ---------- */
export const ARTICLE = {
  category: 'Asthma essentials',
  title: 'What is a preventer inhaler?',
  minutes: 3,
  keyPoints: ['Preventers calm the swelling in your airways over time', 'They work best taken every day — even when you feel well', 'Most preventers are brown, red or orange'],
  paragraphs: ["A preventer inhaler is the one you take every day, even when your breathing feels completely fine. It works quietly in the background, reducing the swelling and sensitivity in your airways so they're less likely to react to triggers.", "Unlike your reliever (usually the blue one), a preventer doesn't give instant relief. Its protection builds up over days and weeks — which is why taking it regularly matters more than taking it perfectly.", "If you're not sure which of your inhalers is the preventer, your care plan lists them — or just ask me and I'll find it for you."],
  related: [{
    title: 'Spotting your asthma triggers',
    minutes: 5,
    tint: 'coral' as Tint
  }, {
    title: 'Living well in pollen season',
    minutes: 4,
    tint: 'sage' as Tint
  }]
};
/* ---------- health library (feature screen) ---------- */
export type Article = {
  id: string;
  category: string;
  title: string;
  minutes: number;
  narrated: boolean;
  tint: Tint;
};
export const LIBRARY_CATEGORIES = ['All', 'Asthma', 'Allergies', 'Sleep', 'Wellbeing'];
export const LIBRARY_ARTICLES: Article[] = [{
  id: 'preventer',
  category: 'Asthma essentials',
  title: 'What is a preventer inhaler?',
  minutes: 3,
  narrated: true,
  tint: 'teal'
}, {
  id: 'triggers',
  category: 'Asthma essentials',
  title: 'Spotting your asthma triggers',
  minutes: 5,
  narrated: true,
  tint: 'coral'
}, {
  id: 'pollen-season',
  category: 'Allergies',
  title: 'Living well in pollen season',
  minutes: 4,
  narrated: false,
  tint: 'sage'
}, {
  id: 'sleep-breathing',
  category: 'Sleep',
  title: 'Breathing exercises for better sleep',
  minutes: 6,
  narrated: true,
  tint: 'lilac'
}, {
  id: 'peak-flow',
  category: 'Asthma essentials',
  title: 'Understanding your peak flow numbers',
  minutes: 4,
  narrated: true,
  tint: 'sky'
}, {
  id: 'cold-weather',
  category: 'Wellbeing',
  title: 'Staying active in cold weather',
  minutes: 3,
  narrated: false,
  tint: 'butter'
}];